import styled, { keyframes, css } from 'styled-components';

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const PANEL_Z = 1000;

export const Panel = styled.div`
  position: fixed;
  right: 24px;
  bottom: 88px;
  width: 400px;
  height: min(640px, calc(100vh - 112px));
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  z-index: ${PANEL_Z};
  animation: ${rise} 0.18s ease-out;

  @media (max-width: 600px) {
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100dvh;
    border-radius: 0;
    border: none;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ theme }) => theme.colors.background.tertiary};
  min-height: 52px;
`;

export const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const HeaderSub = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
`;

export const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

/* ── ticket list ───────────────────────────────────────────── */

export const TicketRow = styled.button<{ $unread?: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: block;
  width: 100%;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 10px;
  margin-bottom: 6px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.border.secondary};
    background: ${({ theme }) => theme.colors.background.hover};
  }

  ${({ $unread, theme }) =>
    $unread &&
    css`
      border-color: rgba(14, 203, 129, 0.45);
      box-shadow: inset 3px 0 0 ${theme.colors.trading.buy};
    `}
`;

export const TicketRowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TicketSubject = styled.span<{ $unread?: boolean }>`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ $unread }) => ($unread ? 700 : 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TicketMeta = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

export const TicketPreview = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.6;
`;

/* ── thread ────────────────────────────────────────────────── */

export const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const MessageRow = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  animation: ${fadeUp} 0.16s ease-out;
`;

export const Bubble = styled.div<{ $mine: boolean }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $mine }) => ($mine ? '12px 12px 4px 12px' : '12px 12px 12px 4px')};
  background: ${({ $mine, theme }) =>
    $mine ? 'rgba(14, 203, 129, 0.16)' : theme.colors.background.tertiary};
  border: 1px solid
    ${({ $mine, theme }) => ($mine ? 'rgba(14, 203, 129, 0.35)' : theme.colors.border.primary)};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const BubbleMeta = styled.div`
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

export const SystemLine = styled.div`
  align-self: center;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 2px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background.primary};
`;

export const AttachmentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

export const Thumb = styled.div`
  width: 132px;
  height: 96px;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const VideoBox = styled.div`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};

  video {
    width: 100%;
    max-height: 220px;
    display: block;
  }
`;

export const FileCaption = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
`;

/* ── composer ──────────────────────────────────────────────── */

export const ComposerRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

export const PendingFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const PendingFile = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.xs};

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const RemoveFile = styled.button`
  all: unset;
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  cursor: pointer;
`;

export const Hint = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

export const ClosedNote = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 6px 0;
`;

export const FieldLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin: 10px 0 4px;
`;

/* ── drag and drop ─────────────────────────────────────────── */

/** Covers the whole panel while a file is held over it. Not interactive: pointer-events would
 *  steal the dragleave/drop events from the panel underneath and strand the overlay on screen. */
export const DropOverlay = styled.div`
  position: absolute;
  inset: 8px;
  z-index: 5;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px dashed ${({ theme }) => theme.colors.trading.buy};
  border-radius: 12px;
  background: rgba(11, 14, 17, 0.88);
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md};
  animation: ${fadeUp} 0.12s ease-out;
`;

export const DropOverlayHint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

/** The empty space under "Details" in the new-report form, turned into a real target. */
export const DropZone = styled.button`
  all: unset;
  box-sizing: border-box;
  flex: 1;
  min-height: 120px;
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  cursor: pointer;
  border-radius: 10px;
  border: 1px dashed ${({ theme }) => theme.colors.border.secondary};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.border.focus};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export const DropZoneIcon = styled.div`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.trading.buy};
  opacity: 0.8;
`;

export const DropZoneHint = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

/** Body of the new-report form: a column so the drop zone can take the leftover height. */
export const FormBody = styled(Body)`
  display: flex;
  flex-direction: column;
`;
